import { aiApiKeyHeaders, isAiBaseUrl } from "./aiApiBaseUrl"

const BASE_URL =
  (process.env.REACT_APP_SERVER_URL || "https://pipelines-dev.rategainsoho.com") +
  "/api/v1"

let tokenProvider = null
let logoutHandler = null

// Set token provider from App component after store initialization
export const setTokenProvider = (provider) => {
  tokenProvider = provider
}

export const setLogoutHandler = (handler) => {
  logoutHandler = handler
}

// Get token from Redux provider, then persisted session storage.
export const getAuthToken = () => {
  const fromProvider = tokenProvider ? tokenProvider() : null
  if (fromProvider) return fromProvider
  if (typeof window === "undefined") return null
  return (
    localStorage.getItem("auth_token") ||
    sessionStorage.getItem("auth_token") ||
    localStorage.getItem("token") ||
    null
  )
}

// Generic request function with automatic auth
export const apiRequest = async (endpoint, options = {}) => {
  const { skipAuth, ...restOptions } = options
  const token = !skipAuth ? getAuthToken() : null

  const isFormData = restOptions.body instanceof FormData

  const config = {
    method: restOptions.method || "GET",

    headers: {
      // Don't set content-type for FormData
      ...(!isFormData && { "Content-Type": "application/json" }),
      ...(token && { Authorization: `Bearer ${token}` }),
      ...restOptions.headers,
    },

    // Must explicitly pass body
    body: restOptions.body,
    ...(restOptions.cache ? { cache: restOptions.cache } : {}),
  }

  const url = endpoint.startsWith("http")
    ? endpoint
    : `${BASE_URL}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`

  if (isAiBaseUrl(url)) {
    Object.assign(config.headers, aiApiKeyHeaders())
  }

  try {
    const response = await fetch(url, config)

    if (response.status === 401 && !skipAuth) {
      // Fast Refresh can fire before a token is attached. Do not treat that as logout.
      if (!token) {
        throw new Error("Authentication not ready")
      }
      // Report 401s can be permission/data gaps, not an expired JWT.
      const isReportCall = /\/report\//.test(url)
      if (!restOptions.skipSessionExpire && !isReportCall && logoutHandler) {
        logoutHandler()
      }
      throw new Error("Session expired. Please login again.")
    }

    if (response.status === 413) {
      throw new Error("File too large. Please reduce file size and try again.")
    }

    if (response.status === 403) {
      // throw new Error("Access denied. Please check your permissions.")
    }

    if (response.status === 422) {
      const errorText = await response.text().catch(() => "Validation failed")
      throw new Error(`Validation error: ${errorText}`)
    }

    if (!response.ok) {
      let errorMessage = "Request failed"
      let errorCode = null
      try {
        const errorData = await response.json()
        errorMessage = errorData?.message || errorData?.error || errorMessage
        errorCode = errorData?.code ?? null
      } catch {
        const errorText = await response.text().catch(() => "")
        if (errorText) errorMessage = errorText
      }
      const err = new Error(errorMessage)
      if (errorCode) err.code = errorCode
      throw err
    }

    const contentType = response.headers.get("content-type")
    const contentLength = response.headers.get("content-length")

    if (contentLength === "0" || response.status === 204) {
      return null
    }

    const text = await response.text()
    if (!text.trim()) {
      return null
    }

    const jsonContentType =
      contentType?.includes("application/json") || contentType?.includes("text/json")

    if (jsonContentType) {
      try {
        return JSON.parse(text)
      } catch {
        return text.trim()
      }
    }

    // Some APIs return JSON-shaped or plain-text bodies without JSON Content-Type (e.g. "ANALYZED").
    const t = text.trim()
    try {
      if (t.startsWith("{") || t.startsWith("[") || (t.startsWith('"') && t.endsWith('"'))) {
        return JSON.parse(text)
      }
    } catch {
      /* return plain text below */
    }

    return t
  } catch (error) {
    throw error
  }
}

// Convenience methods
export const get = (endpoint, options = {}) => {
  return apiRequest(endpoint, {
    method: "GET",
    ...options,
  });
};

export const post = (endpoint, data, options = {}) =>
  apiRequest(endpoint, {
    method: "POST",
    body: data instanceof FormData ? data : JSON.stringify(data),
    ...options,
  })

export const put = (endpoint, data, options = {}) =>
  apiRequest(endpoint, {
    method: "PUT",
    body: JSON.stringify(data),
    ...options,
  })

export const patch = (endpoint, data, options = {}) => {
  const isFormData = data instanceof FormData

  return apiRequest(endpoint, {
    method: "PATCH",
    body: isFormData ? data : JSON.stringify(data), // ⬅️ FIX
    ...options,
  })
}

export const del = (endpoint, options = {}) => apiRequest(endpoint, { method: "DELETE", ...options })

// Build full URL for external redirects
export const buildUrl = (endpoint) => {
  return endpoint.startsWith("http") ? endpoint : `${BASE_URL}${endpoint.startsWith("/") ? endpoint : `/${endpoint}`}`
}
